import React, { useState, useCallback, useEffect } from 'react'
import { Select, Upload, UploadProps, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ButtonCommon, IconWrap, InputCommon, PageHeader } from '../../components'
import contractHEader from '../../assets/images/contract.png'
import clsx from 'clsx'
import styles from './index.module.scss'
import { UploadOutlined } from '@ant-design/icons'
import { SearchIcon, TypeIcon } from '../../assets/icons'
import { ModalContractorDetail } from './DetailContractor'
import { DataEmpty } from '../../components/DataEmpty'
import { Loading } from '../../components/Loading'
import { openNotificationWithIcon, useGetContractorManager, useImportContractors } from '../../hooks'
import debounce from 'lodash.debounce'
import { PaginationCommon } from '../../components/Pagination'
import { RcFile } from 'antd/es/upload'
import { ContractorsMessage } from '../../until/text/messages'
import { IContractor } from '../../models'
import { getFLGType, getTypeMeter, LanguageEnum, SIZE_5MB } from '../../until/constant'
import { useTranslation } from 'react-i18next'
import { getColumnTitle } from '../../until/helper/getColumnTitle'
import CustomTable from '../../components/Table'

export const ContractorTableKey = {
  id: 'id',
  managementAssociationNo: 'management_association_no',
  buildingNo: 'building_no',
  buildingName: 'building_name',
  roomNo: 'room_no',
  customerName: 'customer_name',
  waterCaliber: 'water_caliber',
  companyName: 'company_name',
  subjectCode: 'subject_code',
  FLG: 'first_move_in_flg',
}

export const Contractors = () => {
  const [visible, setVisible] = useState(false)
  const [isLoadingUpload, setIsLoadingUpload] = useState(false)
  const [currentContractorsSelected, setCurrentContractorsSelected] = useState<IContractor | null>(null)
  const [params, setParams] = useState<any>({
    page: 1,
    page_size: 100,
    text_search: '',
  })
  const { t, i18n } = useTranslation()
  const { data, isLoading, isFetching, refetch } = useGetContractorManager(params)

  useEffect(() => {
    refetch()
  }, [i18n.language, refetch])

  const currentLang = i18n.language as LanguageEnum

  const mutateImportCsv = useImportContractors()
  const typeMeter = getTypeMeter(t)
  const FLGType = getFLGType(t)

  const columns: ColumnsType<any> = [
    {
      title: getColumnTitle('contractors.subject_code'),
      dataIndex: ContractorTableKey.subjectCode,
      key: ContractorTableKey.subjectCode,
      width: 110,
    },
    {
      title: getColumnTitle('contractors.management_association_no'),
      dataIndex: ContractorTableKey.managementAssociationNo,
      key: ContractorTableKey.managementAssociationNo,
      width: currentLang === LanguageEnum.JAPANESE ? 100 : 215,
    },
    {
      title: getColumnTitle('contractors.building_no'),
      dataIndex: ContractorTableKey.buildingNo,
      key: ContractorTableKey.buildingNo,
      width: 100,
    },
    {
      title: getColumnTitle('contractors.building_name'),
      dataIndex: ContractorTableKey.buildingName,
      key: ContractorTableKey.buildingName,
      width: 140,
      render: (text) => {
        if (text === null) {
          return ''
        }
        return (
          <div className={styles.text__name__container}>
            <Tooltip title={text} placement="bottomLeft" overlayStyle={{ minWidth: 50 }}>
              <span>{text}</span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: getColumnTitle('contractors.room_no'),
      dataIndex: ContractorTableKey.roomNo,
      key: ContractorTableKey.roomNo,
      width: 100,
    },
    {
      title: getColumnTitle('contractors.customer_name'),
      dataIndex: ContractorTableKey.customerName,
      key: ContractorTableKey.customerName,
      width: 125,
      render: (text) => {
        if (text === null) {
          return ''
        }
        return (
          <div className={styles.text__name__container}>
            <Tooltip title={text} placement="bottomLeft" overlayStyle={{ minWidth: 50 }}>
              <span>{text}</span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: getColumnTitle('contractors.water_caliber'),
      dataIndex: ContractorTableKey.waterCaliber,
      key: ContractorTableKey.waterCaliber,
      width: 135,
    },
    {
      title: getColumnTitle('contractors.first_move_in_flg'),
      dataIndex: ContractorTableKey.FLG,
      key: ContractorTableKey.FLG,
      width: 140,
      render: (value: number) => FLGType[value as 0 | 1],
    },
    {
      title: '',
      dataIndex: 'viewDetail',
      key: 'viewDetail',
      width: 100,
      render: (_, record: IContractor) => (
        <div className={styles.view_detail} style={{ whiteSpace: 'nowrap' }} onClick={() => showViewDetail(record)}>
          {t('contractors.viewDetail')}
        </div>
      ),
    },
  ]

  const showViewDetail = (contractor: IContractor) => {
    setCurrentContractorsSelected(contractor)
    setVisible(!visible)
  }

  const onChangePagination = (page: number, page_size: number) => {
    if (page_size !== params.page_size) {
      return setParams({ ...params, page: 1, page_size })
    }
    setParams({ ...params, page, page_size })
  }

  const onHandleSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSearch(e.target.value)
  }

  const debouncedSearch = useCallback(
    debounce((nextValue) => setParams((prev: any) => ({ ...prev, text_search: nextValue, page: 1 })), 500),
    [],
  )

  // eslint-disable-next-line no-unused-vars
  const handleChangeSelectType = (value: number) => {
    setParams({ ...params, page: 1, type: value })
  }

  const props: UploadProps = {
    name: 'file',
    showUploadList: false,
    accept: 'text/csv',
    headers: {
      authorization: 'authorization-text',
    },
    beforeUpload(file: RcFile) {
      const isCSV = file.type === 'text/csv'
      const isLt5M = file.size < SIZE_5MB
      if (!isCSV) {
        openNotificationWithIcon(
          'error',
          '',
          `${t('contractors.message.invalidCsv')}\n${t('contractors.message.invalidCsv2')}`,
        )
        return isCSV
      }
      if (!isLt5M) {
        openNotificationWithIcon('error', '', t('contractors.message.csv5M'))
        return isLt5M
      }
    },
    onChange(info) {
      if (info.file.status === 'done') {
        openNotificationWithIcon('success', '', `${info.file.name} ${ContractorsMessage.upLoadSuccess}`)
      } else if (info.file.status === 'error') {
        openNotificationWithIcon('error', '', `${info.file.name} ${ContractorsMessage.upLoadFail}`)
      }
    },
    customRequest: async (options: any) => {
      const { file } = options
      const fmData = new FormData()
      fmData.append('file', file)
      try {
        setIsLoadingUpload(true)
        const data = await mutateImportCsv.mutateAsync(fmData)
        openNotificationWithIcon('success', '', data.data)
      } catch (err: any) {
        openNotificationWithIcon('error', '', err.message)
      }
      setIsLoadingUpload(false)
    },
  }

  return (
    <div className={clsx(styles.contract_person)}>
      <PageHeader title={t('contractors.contractor')} backgroundImage={contractHEader} height={'108px'}>
        <div className={styles.contract_person__header}>
          <InputCommon
            suffix={<IconWrap icon={<SearchIcon />} />}
            placeholder={t('contractors.search')}
            className={styles.contract_person__header__search__input}
            onChange={onHandleSearch}
          />
          <Upload {...props}>
            <ButtonCommon icon={<UploadOutlined />} className={styles.contract_person__header__button} type="primary">
              {t('contractors.importCSV')}
            </ButtonCommon>
          </Upload>
        </div>
      </PageHeader>
      <Loading isLoading={isLoadingUpload || isLoading || isFetching} />
      <div className={clsx(styles.page__content__filter)}>
        <div
          className={clsx(
            styles.group__filter,
            {
              [styles.hidden]: data?.data.length === 0 || isLoading || isFetching,
            },
            'select__group',
          )}
        >
          <Select
            style={{ minWidth: 160 }}
            allowClear
            optionLabelProp="label"
            placeholder={
              <>
                <IconWrap color="#8C8C8C" icon={<TypeIcon />} className={clsx(styles.icon__filter, styles.stafficon)} />
                {t('contractors.type')}
              </>
            }
            onChange={handleChangeSelectType}
            value={params.type}
          >
            {typeMeter.map((item: { name: string; value: number }) => (
              <Select.Option
                key={item.value}
                value={item.value}
                className="select__option"
                label={
                  <React.Fragment>
                    <IconWrap color="#8C8C8C" icon={<TypeIcon />} className={clsx(styles.icon__filter)} />
                    {item.name}
                  </React.Fragment>
                }
              >
                <span className={styles.text}>{item.name}</span>
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      {!isLoading && (
        <div className={clsx(styles.page__content)}>
          {data?.data && data.data.length > 0 ? (
            <>
              <CustomTable
                rowKey="id"
                columns={columns}
                dataSource={data?.data}
                pagination={false}
                scroll={{ x: 1000, y: 'calc(100vh - 400px)' }}
              />
              <div className={styles.pagination}>
                <PaginationCommon
                  defaultCurrent={1}
                  total={data.pagination.total}
                  current={data.pagination.current_page}
                  pageSize={data.pagination.per_page}
                  onChangePagination={onChangePagination}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                />
              </div>
            </>
          ) : (
            <DataEmpty detail={t('contractors.noResultsMessage')} className={styles.empty__results}>
              <Upload {...props}>
                <ButtonCommon icon={<UploadOutlined />} type="primary">
                  {t('contractors.importCSV')}
                </ButtonCommon>
              </Upload>
            </DataEmpty>
          )}
        </div>
      )}
      <ModalContractorDetail
        info={currentContractorsSelected!}
        title={t('contractors.detail')}
        visible={visible}
        onOk={() => setVisible(false)}
        onCancel={() => setVisible(false)}
      />
    </div>
  )
}
